import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import saveAs from "file-saver";
import { combineLatest, merge, Observable } from "rxjs";
import { filter, first, map, mergeMap, switchMap } from "rxjs/operators";
import {
  selectCirculationLoaded,
  selectUploadDocumentCompleted,
  selectUploadDocumentFailed
} from "../circulation/state";
import { CirculationDocumentActions } from "../circulation/state/actions";
import {
  AbstractCirculationsService,
  CirculationId,
  EntityId,
  IDocument,
  IDocumentUploadEndpoints,
  IHyperlinkToSave
} from "../interfaces";
import { LogClass, Logger } from "../logger";
import { selectCirculationId } from "../state";
import { StepFacade } from "../step/services/step.facade";

@LogClass({ source: "DocumentFacade" })
@Injectable({
  providedIn: "root"
})
export class DocumentFacade {
  private logger: Logger;
  constructor(
    private circulationService: AbstractCirculationsService,
    private store: Store,
    private stepFacade: StepFacade,
    private http: HttpClient
  ) { }

  public addHyperlink(
    doc: IHyperlinkToSave,
    circId: CirculationId,
    stepNo?: number
  ): Observable<IDocument> {
    return this.circulationService.AddHyperlink(doc, circId, stepNo);
  }

  public remove(circId: CirculationId, docId: string): void {
    this.store.dispatch(
      CirculationDocumentActions.removeDocument({ id: circId, docId: docId })
    );
  }

  public download(id: CirculationId, doc: IDocument): Observable<number> {
    return this.circulationService.DownloadDocument(id, doc.id).pipe(
      map((b) => {
        saveAs(b, doc.name);
        return 1;
      })
    );
  }

  public downloadAll(id: CirculationId): Observable<number> {
    return this.circulationService.GetDocuments(id).pipe(
      first(),
      map((docs) => {
        return docs.filter((d) => !d.isHyperlink);
      }),
      mergeMap((docs) => {
        return combineLatest(
          docs.map((doc) => {
            return this.circulationService.DownloadDocument(id, doc.id).pipe(
              map((b) => {
                saveAs(b, doc.name);
                return true;
              })
            );
          })
        );
      }),
      map((b) => b.length)
    );
  }

  public open(circId: EntityId, doc: IDocument): void {
    if (doc.isHyperlink) {
      window.open(doc.uri, '_blank');
      return;
    }
    this.circulationService.openDocument(circId, doc);
  }

  public getForCirculation(id: CirculationId): Observable<IDocument[]> {
    return this.circulationService.GetDocuments(id);
  }

  public getForCurrentCirculation(): Observable<IDocument[]> {
    return merge(this.getUploadCompleted(), this.getCirculationLoaded()).pipe(
      filter((x) => !!x),
      switchMap(() => this.store.select(selectCirculationId)),
      switchMap((id) => this.getForCirculation(id))
    );
  }

  public getCirculationLoaded(): Observable<boolean> {
    return this.store.select(selectCirculationLoaded);
  }
  public getUploadEndpointsForCurrentCirculation(): Observable<IDocumentUploadEndpoints> {
    const circulation = this.store.select(selectCirculationId);
    const step = circulation.pipe(
      switchMap((c) => {
        return this.stepFacade.getStepsForCirculation(c);
      }),
      map((s) => s.filter((x) => x.props.status == "InProgress")[0] || null)
    );
    return combineLatest([circulation, step]).pipe(
      switchMap(([id, s]) => {
        return this.getUploadEndpoints(id, s?.props.stepNo || 0);
      })
    );
  }

  public getUploadEndpoints(
    id: CirculationId,
    stepNo: number
  ): Observable<IDocumentUploadEndpoints> {
    return this.circulationService.GetDocumentUploadEndpoints(id, stepNo);
  }

  public getUploadCompleted(): Observable<boolean> {
    return this.store.select(selectUploadDocumentCompleted);
  }

  public getUploadFailed(): Observable<boolean> {
    return this.store.select(selectUploadDocumentFailed);
  }

  public uploadCompleted(): void {
    this.store.dispatch(CirculationDocumentActions.uploadDocumentCompleted());
    this.logger.debug("Document upload completed.");
  }

  public uploadFailed(error: ErrorEvent): void {
    this.store.dispatch(CirculationDocumentActions.uploadDocumentFailed());
    this.logger.error("Document upload failed.", error);
  }

  public uploadStarted(): void {
    this.store.dispatch(CirculationDocumentActions.uploadDocumentStarted());
    this.logger.debug("Document upload started.");
  }
}
